/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { TextInput, TextInputSkeleton } from '@carbon/react';
import _ from 'lodash';
import React, { useRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { messages } from './messages';

const MAX_LENGTH = 100;

interface HeaderNameInputProps {
  /**
   *
   */
  name: string;
  /**
   *
   */
  updateName: (name: string, isValid?: boolean, isDirty?: boolean) => void;
  /**
   *
   */
  loading: boolean;
  /**
   *
   */
  useSplashPage?: boolean;
  /**
   *
   */
  readOnly: boolean;
}
export const HeaderNameInput = ({
  name,
  updateName,
  loading,
  useSplashPage,
  readOnly
}: HeaderNameInputProps) => {
  const intl = useIntl();
  const {
    register,
    watch,
    formState: { errors, isDirty, isValid }
  } = useForm({
    mode: 'onChange'
  });

  const nameInputRef = useRef(null);

  const nameValue = watch('name');

  useEffect(() => {
    if (useSplashPage) {
      nameInputRef.current?.focus();
    }
  }, [nameInputRef, useSplashPage]);

  // Check for changes and update the name in store.
  useEffect(() => {
    if (!readOnly && !loading) {
      // The formState's isValid fluctuates between false and true
      // during loading time, so we need to check whether there are
      // errors in the name itself.
      const nameIsInvalid = !isValid && !_.isNil(errors.name);
      updateName(nameValue, !nameIsInvalid, isDirty);
    }
  }, [errors.name, readOnly, loading, nameValue, isDirty, isValid, updateName]);

  return (
    <div className="Panel--header-white">
      <section className="Panel--IntegrationName bx--grid">
        <div className="bx--row">
          <div className="bx--col bx--col-sm-4 bx--col-md-4 bx--col-lg-4 bx--col-xlg-4">
            <div className="TabbedHeader__field-container">
              {loading ? (
                <TextInputSkeleton />
              ) : (
                name && (
                  <TextInput
                    id="integration_meta_name"
                    defaultValue={name}
                    ref={nameInputRef}
                    labelText={intl.formatMessage(messages.nameLabel)}
                    light
                    invalid={!!errors.name}
                    invalidText={errors.name?.message}
                    disabled={readOnly}
                    {...register('name', {
                      required: intl.formatMessage(messages.nameRequired),
                      maxLength: {
                        value: MAX_LENGTH,
                        message: intl.formatMessage(messages.nameLengthError, {
                          length: MAX_LENGTH
                        })
                      }
                    })}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
